/**
 * Header Anchor Navigation
 *
 * Handles two scenarios:
 * 1. Click on a hash link in desktop or mobile header menus:
 *    - If the target ID exists on the current page → smooth scroll to it.
 *    - If not → navigate to the homepage root with that hash.
 *
 * 2. Page load with a hash in the URL (e.g. arriving at /#whats-inside):
 *    - Retry scrolling every 100 ms for up to 3 s until the element appears
 *      (accounts for sections that render/animate after DOMContentLoaded).
 */

(function () {
  function isSafari() {
    return document.documentElement.classList.contains('safari');
  }

  /**
   * Extract a hash fragment from any href value.
   * Handles both pure hashes "#foo" and full same-origin URLs "https://shop.com/pages/x#foo".
   * Returns the hash INCLUDING the "#" prefix, or null if none.
   */
  function getHash(href) {
    if (!href) return null;
    try {
      const url = new URL(href, window.location.origin);
      return url.hash ? decodeURIComponent(url.hash) : null; // e.g. "#whats-inside"
    } catch {
      return null;
    }
  }

  /**
   * Returns true if the href points to the homepage (with or without hash).
   */
  function isHomepageUrl(href) {
    try {
      const url = new URL(href, window.location.origin);
      const rootUrl = window.Shopify?.routes?.root || '/';
      return (
        url.origin === window.location.origin &&
        (url.pathname === rootUrl || url.pathname === rootUrl.replace(/\/$/, '') || url.pathname === '/')
      );
    } catch {
      return false;
    }
  }

  /** Scroll to an element, accounting for the sticky header height. */
  function scrollToElement(el, smooth = true) {
    const headerHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      ) || 0;
    const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
    if (isSafari()) {
      window.scrollTo(0, Math.max(0, top));
      return;
    }
    window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
  }

  /** Click handler attached to every hash anchor in the header. */
  function onAnchorClick(event) {
    const link = event.currentTarget;
    const href = link.getAttribute('href');
    const hash = getHash(href);

    if (!hash) return; // not a hash link

    const targetId = hash.slice(1); // strip the "#"
    const target = document.getElementById(targetId);

    if (target) {
      event.preventDefault();
      scrollToElement(target, !isSafari());
    } else {
      // Element not on this page.
      // If the link is purely a hash (e.g. href="#whats-inside"), navigate to homepage.
      if (href.startsWith('#')) {
        event.preventDefault();
        const rootUrl = window.Shopify?.routes?.root || '/';
        window.location.href = rootUrl + hash;
      }
      // Otherwise, let the browser handle it natively.
    }
  }

  /** Attach click listeners to all hash-only links inside header/drawer. */
  function bindHeaderLinks() {
    // Covers both desktop nav and mobile drawer (menu-drawer)
    const selectors = [
      '.section-header a[href]',
      '.header-wrapper a[href]',
      'header-drawer a[href]',
      'menu-drawer a[href]',
    ].join(', ');

    const links = document.querySelectorAll(selectors);

    for (const link of links) {
      const href = link.getAttribute('href');
      const hash = getHash(href);
      if (!hash) continue; // skip non-hash links

      // Only handle links that either are pure hashes or point to homepage
      if (href.startsWith('#') || isHomepageUrl(href)) {
        // Remove previously added listener to avoid duplicates
        link.removeEventListener('click', onAnchorClick);
        link.addEventListener('click', onAnchorClick);
      }
    }
  }

  /**
   * On page load: if the URL has a hash, poll until the target element
   * appears in the DOM and then scroll to it.
   */
  function scrollToHashOnLoad() {
    let hash = window.location.hash;
    if (!hash) return;

    try {
      hash = decodeURIComponent(hash);
    } catch {}

    const targetId = hash.slice(1);
    const maxAttempts = 30; // 30 × 100 ms = 3 s
    let attempts = 0;

    function attempt() {
      const el = document.getElementById(targetId);
      if (el) {
        scrollToElement(el, false); // Instant scroll on load, Safari iOS blocks smooth scroll without user gesture
        return;
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(attempt, 100);
      }
    }

    // Start after a short initial delay so layouts can settle
    setTimeout(attempt, 200);
  }

  function init() {
    if (!isSafari()) {
      bindHeaderLinks();
      scrollToHashOnLoad();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
